import {
  COACH_MARKETPLACE_PAGE,
  VAIR_INTEGRATED_EVENT_CREATE_PAGE,
  VAIR_RATING_SESSION_CREATE_PAGE,
} from 'constants/pages';
import PlusPlain from 'svg/PlusPlain';
import { ButtonLink } from 'components/Button';
import classNames from 'styles/utils/classNames';
import { useVairProStats } from './useVairProStats';

// Existing repo assets reused (no new downloads).
const BADGE_SRC = '/images/player-logged-in-v2/vair-pro/vair-pro-white.png';
const TROPHY_SRC = '/images/vair/vair-tournament.png';
const MAP_SRC = '/images/player-logged-in-v2/vair-pro/map.png';
// TODO(asset): blue "Meter" gauge — could not find an existing match; please
// point me to it. Temp asset used so the layout renders.
const METER_SRC = '/images/player-logged-in-v2/vair-pro/meter.png';

interface Props {
  /** VAIR ambassador → can create integrated events. */
  isAmbassador: boolean;
  /** Active VAIR Pro (per-sport) → can run rating sessions (sees both CTAs). */
  isVairPro: boolean;
}

interface StatProps {
  title: string;
  value: string;
  /** Small icon shown to the right of the value (meter / trophy). */
  iconSrc: string;
  buttonLabel: string;
  /** Quick-create entry for this stat (carries the ?vairFlow= param). */
  href: string;
}

// One stat column: title, big number + icon, and a white "create" pill.
function Stat({ title, value, iconSrc, buttonLabel, href }: StatProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-3">
      <p className="typography-product-subheading text-color-text-lightmode-invert">{title}</p>
      <div className="flex items-start gap-1">
        <p className="typography-product-display-desktop text-color-text-lightmode-invert">
          {value}
        </p>
        <img src={iconSrc} alt="" className="size-5 shrink-0" />
      </div>
      <ButtonLink
        href={href}
        variant="secondary"
        size="md"
        isInlineDesktop
        iconLeft={<PlusPlain className="size-4" />}
      >
        {buttonLabel}
      </ButtonLink>
    </div>
  );
}

// "VAIR PRO" dashboard card. Sits above UpcomingSection on the logged-in player
// home. Counts come from useVairProStats (hosted events).
// VAIR Pros see both CTAs; ambassador-only sees just the integrated-event CTA.
export default function VairProSection({ isAmbassador, isVairPro }: Props) {
  // Fetched here rather than in PlayerLoggedInV2 so the request only fires for
  // users who actually see the card.
  const { ratingSessions, integratedEvents, statsResolved } = useVairProStats();
  // An em dash until the counts settle — never render a placeholder 0.
  const formatCount = (count: number) => (statsResolved ? String(count) : '—');

  return (
    <section className="container mx-auto">
      {/* Stacked until xl, where there's room for badge gutter + 2 stats + map
          without the right-aligned content sliding under the absolute badge.
          The gutter is only reserved when the badge actually renders. */}
      <div
        className={classNames(
          'relative flex flex-col gap-6 overflow-hidden rounded-3xl border border-color-border-input-lightmode bg-color-bg-lightmode-invert p-6 shadow-[0px_16px_40px_0px_rgba(53,62,88,0.16),0px_4px_16px_0px_rgba(0,0,0,0.08)] xl:flex-row xl:items-center xl:gap-12',
          isVairPro && 'xl:pl-[10rem]',
        )}
      >
        {/* VAIR PRO badge — pinned top-left. Gated on isVairPro: the badge asserts
            an earned credential, so an ambassador-only card must not show it. */}
        {isVairPro && (
          <img
            src={BADGE_SRC}
            alt="VAIR Pro"
            className="absolute left-4 top-0 z-10 h-20 w-auto drop-shadow-[0_4px_20px_rgba(0,0,0,0.4)] sm:left-12"
          />
        )}

        {/* Stats */}
        <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:gap-20">
          {isVairPro && (
            <Stat
              title="VAIR Rating Sessions"
              value={formatCount(ratingSessions)}
              iconSrc={METER_SRC}
              buttonLabel="Create VAIR rating session"
              href={VAIR_RATING_SESSION_CREATE_PAGE}
            />
          )}
          {(isAmbassador || isVairPro) && (
            <Stat
              title="VAIR integrated events"
              value={formatCount(integratedEvents)}
              iconSrc={TROPHY_SRC}
              buttonLabel="Create VAIR integrated event"
              href={VAIR_INTEGRATED_EVENT_CREATE_PAGE}
            />
          )}
        </div>

        {/* Map → view profile on marketplace */}
        <div className="relative flex h-[129px] items-center justify-center overflow-hidden rounded-xl xl:ml-auto xl:h-[125px] xl:w-[337px] xl:self-stretch">
          <img src={MAP_SRC} alt="" className="absolute inset-0 size-full object-cover" />
          <ButtonLink
            href={COACH_MARKETPLACE_PAGE}
            variant="primary"
            size="md"
            isInline
            className="relative z-10 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]"
          >
            View profile on marketplace&nbsp;<span aria-hidden>📍</span>
          </ButtonLink>
        </div>
      </div>
    </section>
  );
}
